import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AppTextService } from '@fi-shared/app-text';
import { ILinkToType, LinkToTypesService } from '@fi-shared/link-to/link-to-types.service';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { LinkToModel } from '@fi-shared/link-to/link-to.model';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { SharedActionsBaseService } from '@fi-shared/fi-shared-services/shared-actions-base.service';
import { FiChecklist2Service } from '@fi-shared/checklist/fi-checklist2.service';
import { isDefined } from '@fi-shared/utils';
import { ITextEditorInsertLinkDialogData } from '@fi-shared/fi-text-editor/fi-text-editor-insert-link/text-editor-insert-link';
import { INameIdObjWithChildren } from '@fi-shared/fi-shared-interfaces';
import { ITextItemLinkValue } from '@fi-shared/fi-text-item-helper/fi-text-item-link';
import { BehaviorSubject } from 'rxjs';
import { IContactModel } from '@fi-shared/models/common';
import { getDefaultCountryId } from '@fi-app/core/country-id';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';

enum InsertLinkInputType {
    singleInput,
    dropdown,
    foldableDropdown,
    phoneNumberSelect,
}

@Component({
    selector: 'fi-text-editor-insert-link-dialog',
    templateUrl: './text-editor-insert-link-dialog.component.html',
    styleUrls: ['./text-editor-insert-link-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class TextEditorInsertLinkDialogComponent
    extends FiDialogsContentBase<ITextEditorInsertLinkDialogData, LinkToModel>
    implements OnInit
{
    currentSelectLabel: string = '';
    currentNamePropertyName: string = '';
    currentSelectList: {
        Id: number;
    }[] = [];

    get currentFoldableSelectList(): INameIdObjWithChildren[] {
        return this.currentSelectList as INameIdObjWithChildren[];
    }

    selectedLinkValueId: number;
    linkToModel: LinkToModel;
    private readonly emailValidator = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,})+$/;
    protected InsertLinkInputType = InsertLinkInputType;

    selectListLoaded$ = new BehaviorSubject<boolean>(false);

    ngOnInit() {
        this.setServices();

        this.linkToModel = new LinkToModel();
        if (this.data.model) {
            this.linkToModel.Type = this.data.model.Type;
            this.linkToModel.Value = this.data.model.Value;
            this.linkToModel.EditTitle = this.data.title;
        }

        if (!this.linkToModel.Value) {
            this.linkToModel.Value = {};
        }
        if (!this.isWebAddress() && !this.isEmail() && !this.isPhoneNumber()) {
            this.setDefaultInputsData();

            if (this.linkToModel.Value.Id) {
                this.selectedLinkValueId = this.linkToModel.Value.Id;
            }
        } else {
            if (typeof this.linkToModel.Value === typeof '') {
                this.linkToModel.Value = {
                    Name: <any>this.linkToModel.Value,
                };
            }
            if (this.isPhoneNumber()) {
                this.setPhoneNumberContactByLinkData();
            }
        }
    }

    phoneNumberContact: IContactModel = {};
    private setPhoneNumberContactByLinkData() {
        if (!this.linkToModel.Value.Name?.trim().length) {
            this.phoneNumberContact.ContactValue = '';
            this.phoneNumberContact.CountryId = getDefaultCountryId(this.fiUserDataService.applicationLanguage);
        } else {
            this.phoneNumberContact.ContactValue = this.linkToModel.Value.Name.split(' ')[1];
            this.phoneNumberContact.CountryId = this.linkToModel.Value.Id;
        }
    }
    onPhoneNumberChange() {
        this.linkToModel.Value.Name = `${this.phoneNumberContact.ExtendedValue} ${this.phoneNumberContact.ContactValue}`;
        this.linkToModel.Value.Id = this.phoneNumberContact.CountryId;
    }

    private loadSelectList(loadPromise: Promise<{ Id: number }[]>) {
        this.loadingService.trackPromise(loadPromise).then((response) => {
            this.currentSelectList = response;
            this.selectListLoaded$.next(true);
        });
    }

    setDefaultInputsData() {
        switch (this.linkToModel.Type) {
            case FiLinkToType.CheckList:
                this.currentSelectLabel = this.appTextService.get('insert_link.select_checklist');
                this.currentNamePropertyName = 'Name';
                this.loadSelectList(this.linkToTypesService.getLightWeightCheckLists());
                break;

            case FiLinkToType.ChecklistV2:
                this.currentSelectLabel = this.appTextService.get('insert_link.select_checklist');
                this.currentNamePropertyName = 'Name';
                this.loadSelectList(this.fiChecklist2Service.getChecklistsForInsertLink(false));
                break;

            case FiLinkToType.ChecklistV2Audit:
                this.currentSelectLabel = this.appTextService.get('insert_link.select_checklist');
                this.currentNamePropertyName = 'Name';
                this.loadSelectList(this.fiChecklist2Service.getChecklistsForInsertLink(true));
                break;

            case FiLinkToType.Document:
                this.currentSelectLabel = this.appTextService.get('insert_link.select_document');
                this.currentNamePropertyName = 'Name';
                this.loadSelectList(this.linkToTypesService.getLightWeightDocuments());
                break;

            case FiLinkToType.Media:
                this.currentSelectLabel = this.appTextService.get('insert_link.select_file');
                this.currentNamePropertyName = 'Name';
                this.loadSelectList(this.linkToTypesService.getLightWeightMediaBankFiles());
                break;

            case FiLinkToType.UserProfile:
                this.currentSelectLabel = this.appTextService.get('insert_link.select_colleague');
                this.currentNamePropertyName = 'FullName';
                this.loadSelectList(this.linkToTypesService.getLightWeightUsers());
                break;

            case FiLinkToType.Form:
                this.currentSelectLabel = this.appTextService.get('insert_link.select_form');
                this.currentNamePropertyName = 'Name';
                this.loadSelectList(this.linkToTypesService.getLightWeightForms());
                break;
        }
    }

    getInsertLinkInputType(): InsertLinkInputType {
        if (this.isWebAddress() || this.isEmail()) return InsertLinkInputType.singleInput;

        if ([FiLinkToType.ChecklistV2, FiLinkToType.ChecklistV2Audit].includes(this.linkToModel.Type))
            return InsertLinkInputType.foldableDropdown;

        if (this.isPhoneNumber()) return InsertLinkInputType.phoneNumberSelect;

        return InsertLinkInputType.dropdown;
    }

    isPhoneNumber(): boolean {
        return this.linkToModel.Type === FiLinkToType.PhoneNumber;
    }

    isWebAddress(): boolean {
        return this.linkToModel.Type === FiLinkToType.WebAddress;
    }

    isEmail(): boolean {
        return this.linkToModel.Type === FiLinkToType.Email;
    }

    getSingleInputLabel(): string {
        return this.isWebAddress()
            ? this.appTextService.get('common.url')
            : this.appTextService.get('common.email_address');
    }

    isLinkTextShown(): boolean {
        return !this.isEmail() && !this.isPhoneNumber() && !this.data.isLinkTextHidden;
    }

    selectionChange($event: number) {
        let selectedItem = this.currentSelectList.find((selectItem) => selectItem.Id === $event);
        if (selectedItem) {
            this.onLinkToValueChanged({
                Id: selectedItem.Id,
                Name: selectedItem[this.currentNamePropertyName],
                ObjectType: this.linkToTypesService.getObjectTypeByLinkType(this.linkToModel.Type),
            });
        } else {
            this.onLinkToValueChanged(undefined);
        }
    }

    protected onLinkToValueChanged($event: ITextItemLinkValue) {
        if ($event) {
            this.linkToModel.Value.Id = $event.Id;
            this.linkToModel.Value.Name = $event.Name;
            this.linkToModel.Value.ObjectType = $event.ObjectType;
            this.linkToModel.EditTitle = $event.Name;
        } else {
            delete this.linkToModel.Value.Id;
            delete this.linkToModel.Value.Name;
            delete this.linkToModel.Value.ObjectType;
            delete this.linkToModel.EditTitle;
        }
    }

    isExtendedSearchEnabled(): boolean {
        const linkToType: ILinkToType = this.linkToTypesService.TypesMap[this.linkToModel.Type];

        return this.sharedActionsService.isExtendedSearchEnabled && isDefined(linkToType.extendedSearch);
    }

    openExtendedSearch($event: MouseEvent) {
        $event.preventDefault();

        const linkToType = this.linkToTypesService.TypesMap[this.linkToModel.Type];
        const objectType = this.linkToTypesService.getObjectTypeByLinkType(this.linkToModel.Type);
        this.sharedActionsService.openExtendedSearch(objectType).then((dialog) => {
            if (dialog) {
                this.linkToModel.Value = linkToType.extendedSearch.getValueFromItem(dialog);
                this.selectedLinkValueId = this.linkToModel.Value.Id;
            }
        });
    }

    isYesButtonDisabled(): boolean {
        if (this.isEmail()) {
            return !this.linkToModel.Value?.Name?.match(this.emailValidator);
        }
        if (this.isWebAddress()) {
            return !this.linkToModel.Value?.Name;
        }
        if (this.isPhoneNumber()) {
            return (
                this.phoneNumberContact?.ContactValue?.trim().length < 4 ||
                !isDefined(this.phoneNumberContact.CountryId)
            );
        }
        return !this.linkToModel.Value?.Id;
    }

    getResult(): LinkToModel {
        return this.linkToModel;
    }

    private linkToTypesService: LinkToTypesService;
    private appTextService: AppTextService;
    private loadingService: LoadingService;
    private sharedActionsService: SharedActionsBaseService;
    private fiChecklist2Service: FiChecklist2Service;
    private fiUserDataService: FiUserDataService;

    private setServices() {
        this.linkToTypesService = this.injector.get(LinkToTypesService);
        this.sharedActionsService = this.injector.get(SharedActionsBaseService);
        this.appTextService = this.injector.get(AppTextService);
        this.loadingService = this.injector.get(LoadingService);
        this.fiChecklist2Service = this.injector.get(FiChecklist2Service);
        this.fiUserDataService = this.injector.get(FiUserDataService);
    }
}
